import '../Input/Input.css';

import { ChevronDownIcon } from '@ctct/summit-icons';
import * as SelectPrimitive from '@radix-ui/react-select';
import { cva } from 'class-variance-authority';
import { type ComponentPropsWithoutRef, type ElementRef, forwardRef } from 'react';

import { cn } from '../../lib/utils';
import { type createStyleType } from '../../theme/types';
import { useFieldContext } from '../Field';
import { type InputHeight, inputHeightVariants, inputStyles, inputThemeableVars } from '../Input';

const iconStyles = 'r:h-5 r:w-5 r:shrink-0 r:text-(--icon)';

const themeableSelectTriggerVars = [
  ...inputThemeableVars.base,
  ...inputThemeableVars.error,
  ...inputThemeableVars.focus,
  ...inputThemeableVars.dimension,
  ...inputThemeableVars.textfield,
  'r:[--icon:theme(selectTrigger.icon.default)]',
] as const;
type SelectTriggerStyle = ReturnType<typeof createStyleType<typeof themeableSelectTriggerVars>>;

const selectTriggerVariants = cva(
  `
    ${themeableSelectTriggerVars.join(' ')}
    ${inputStyles.base}
    ${inputStyles.disabled}
    ${inputStyles.textfield}
    rds-select-trigger r:[&>span]:leading-5
    r:[&[data-state=open]>svg]:rotate-180
    r:data-[state=open]:border-(--border-active)
    r:gap-3 r:items-center r:justify-between r:text-left
    r:whitespace-nowrap
    r:overflow-x-hidden
    [&>span]:line-clamp-1
    r:[&>span]:truncate
    r:[&>span]:overflow-hidden
  `,
  {
    variants: {
      variant: inputHeightVariants,
      hasError: {
        true: inputStyles.error,
        false: inputStyles.focus,
      },
    },
    defaultVariants: {
      variant: 'md',
      hasError: false,
    },
  }
);

interface SelectTriggerProps extends ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> {
  /** The height of the select dropdown. */
  variant?: InputHeight;
  /** Indicate if the component should render its error state. */
  hasError?: boolean;
  /** Set inline styles, including the component's built-in CSS variables.  */
  style?: SelectTriggerStyle;
}

const SelectTrigger = forwardRef<ElementRef<typeof SelectPrimitive.Trigger>, SelectTriggerProps>(
  ({ variant = 'md', disabled, hasError, className, children, asChild, ...props }, ref) => {
    const { inputId, labelId, state } = useFieldContext();
    const isDisabled = disabled ?? state === 'disabled';
    const hasEffectiveError = hasError ?? state === 'error';
    return (
      <SelectPrimitive.Trigger
        id={inputId}
        aria-labelledby={labelId}
        disabled={isDisabled}
        ref={ref}
        asChild={asChild}
        className={cn(
          !asChild && selectTriggerVariants({ variant, hasError: hasEffectiveError }),
          className
        )}
        data-sds-id="select-trigger"
        {...props}
      >
        {asChild ? (
          children
        ) : (
          <>
            {children}
            <SelectPrimitive.Icon asChild>
              <ChevronDownIcon className={`${iconStyles}`} />
            </SelectPrimitive.Icon>
          </>
        )}
      </SelectPrimitive.Trigger>
    );
  }
);
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

export { SelectTrigger, selectTriggerVariants };
